import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { CITIES, CITY_SLUGS } from "@/data/cities";
import { OCCASIONS, OCCASION_SLUGS } from "@/data/occasions";
import { LanguageSwitcher } from "@/components/language-switcher";
import { FOOTER_LINK_GROUPS } from "@/data/internal-links";
import { trackEvent } from "@/lib/analytics";



const FOOTER_CITY_SLUGS = CITY_SLUGS.filter((s) => s !== "evangelique");

const CITIES_BY_REGION = FOOTER_CITY_SLUGS.reduce<Record<string, string[]>>(
  (acc, s) => {
    const r = CITIES[s].region;
    if (!acc[r]) acc[r] = [];
    acc[r].push(s);
    return acc;
  },
  {}
);

// Menu desktop épuré : 5 entrées avec 2 menus déroulants
type SubItem = { to: string; label: string; desc?: string };
type NavItem = { label: string; to?: string; children?: SubItem[] };

const PRIMARY_NAV: NavItem[] = [
  { label: "Mariage", to: "/dj-mariage" },
  {
    label: "Prestations",
    children: [
      { to: "/bar-a-parfum", label: "Bar à parfum", desc: "Animation olfactive · dès 12 €/invité" },
      { to: "/soirees-evenements", label: "Soirées & événements", desc: "Anniversaire, entreprise, cérémonie" },
      { to: "/soirees-thematiques", label: "Soirées thématiques", desc: "Années 80, white, fluo, années folles" },
      { to: "/location-gender-reveal", label: "Gender Reveal", desc: "Effets pro · dès 290 €" },
      { to: "/sono-lumiere", label: "Sono & Lumière", desc: "Pack pro avec ou sans DJ" },
    ],
  },
  {
    label: "Location",
    children: [
      { to: "/location-materiel", label: "Location matériel", desc: "Sono, lumière, photobooth, karaoké" },
      { to: "/location-sono-mariage", label: "Pack sono mariage", desc: "Livré & installé, dès 290 €" },
      { to: "/conditions-de-location", label: "Conditions de location", desc: "CGV, caution, livraison" },
    ],
  },
  {
    label: "Zones",
    children: [
      { to: "/dj-avignon", label: "DJ Avignon", desc: "Mariage, soirée privée, entreprise" },
      { to: "/dj-vaucluse", label: "DJ Vaucluse", desc: "Tout le département 84" },
      { to: "/dj-mariage/luberon", label: "DJ mariage Luberon", desc: "Mas et villages perchés" },
      { to: "/dj-mariage/alpilles", label: "DJ mariage Alpilles", desc: "Saint-Rémy, Maussane, Eygalières" },
      { to: "/lieux", label: "Domaines & lieux", desc: "Fiches détaillées par lieu" },
    ],
  },
  { label: "Tarifs", to: "/tarifs" },
  { label: "Réalisations", to: "/realisations" },

  {
    label: "À propos",
    children: [
      { to: "/a-propos", label: "Qui suis-je", desc: "20 ans en Provence" },
      { to: "/le-concept", label: "Le concept", desc: "Provence Sunset & Dancefloor" },
      { to: "/realisations", label: "Réalisations", desc: "12 événements détaillés" },
      { to: "/avis-clients", label: "Avis clients", desc: "5/5 sur 41 avis Google" },
      { to: "/galerie", label: "Galerie photos" },
      { to: "/actualites", label: "Blog & guides" },
      { to: "/guide-mariage-vaucluse", label: "Guide mariage Vaucluse" },
    ],
  },
];

// Menu mobile groupé : mêmes sections que le desktop, repliables
type MobileGroup = { label: string; to?: string; children?: { to: string; label: string }[] };
const MOBILE_GROUPS: MobileGroup[] = [
  { label: "Accueil", to: "/" },
  { label: "Mariage", to: "/dj-mariage" },
  {
    label: "Prestations",
    children: [
      { to: "/bar-a-parfum", label: "Bar à parfum" },
      { to: "/soirees-evenements", label: "Soirées & événements" },
      { to: "/soirees-thematiques", label: "Soirées thématiques" },
      { to: "/location-gender-reveal", label: "Gender Reveal" },
      { to: "/sono-lumiere", label: "Sono & Lumière" },
    ],
  },
  {
    label: "Location",
    children: [
      { to: "/location-materiel", label: "Location matériel" },
      { to: "/location-sono-mariage", label: "Pack sono mariage" },
      { to: "/conditions-de-location", label: "Conditions de location" },
    ],
  },
  {
    label: "Zones d'intervention",
    children: [
      { to: "/dj-avignon", label: "DJ Avignon" },
      { to: "/dj-vaucluse", label: "DJ Vaucluse" },
      { to: "/dj-mariage/luberon", label: "DJ mariage Luberon" },
      { to: "/dj-mariage/alpilles", label: "DJ mariage Alpilles" },
      { to: "/lieux", label: "Domaines & lieux" },
    ],
  },
  { label: "Tarifs", to: "/tarifs" },
  { label: "Calculateur devis", to: "/calculateur" },

  {
    label: "À propos",
    children: [
      { to: "/a-propos", label: "Qui suis-je" },
      { to: "/le-concept", label: "Le concept" },
      { to: "/realisations", label: "Réalisations" },
      { to: "/avis-clients", label: "Avis clients" },
      { to: "/galerie", label: "Galerie photos" },
      { to: "/actualites", label: "Blog & guides" },
      { to: "/guide-mariage-vaucluse", label: "Guide mariage Vaucluse" },
    ],
  },
  { label: "Contact", to: "/contactez-nous" },
];

export function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 20);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  const hoverOpen = (label: string) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenMenu(label);
  };
  const hoverClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenMenu(null), 120);
  };

  // Header toujours sombre et visible, dès le chargement comme au scroll
  const textCls = "text-white";
  const subCls = "text-white/80";
  return (
    <header
      data-surface="dark"
      className={`fixed inset-x-0 top-0 z-50 transition-shadow duration-500 bg-[#0a0a0a] backdrop-blur-md border-b border-[var(--gold-line)] ${
        scrolled ? "shadow-[0_2px_20px_rgba(0,0,0,0.35)]" : ""
      }`}
    >

      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-16 lg:h-20">
        <Link to="/" className="flex items-center gap-3 group">
          <span className={`font-serif-display text-2xl italic ${textCls}`}>
            DJ <span className="text-[var(--gold)]">Val</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {PRIMARY_NAV.map((n) =>
            n.children ? (
              <div
                key={n.label}
                className="relative"
                onMouseEnter={() => hoverOpen(n.label)}
                onMouseLeave={hoverClose}
              >
                <button
                  type="button"
                  onClick={() => setOpenMenu((cur) => (cur === n.label ? null : n.label))}
                  className={`text-[11px] uppercase tracking-[0.25em] hover:text-[var(--gold)] transition-colors flex items-center gap-1.5 ${subCls}`}
                  aria-haspopup="true"
                  aria-expanded={openMenu === n.label}
                >
                  {n.label}
                  <span className="text-[8px] opacity-70">▼</span>
                </button>
                {openMenu === n.label && (
                  <div
                    className="absolute left-1/2 -translate-x-1/2 top-full pt-4 min-w-[320px]"
                    onMouseEnter={() => hoverOpen(n.label)}
                    onMouseLeave={hoverClose}
                  >
                    <div className="bg-[#0a0a0a] backdrop-blur-md border border-[var(--gold-line)] shadow-2xl py-3">
                      {n.children.map((c) => (
                        <Link
                          key={c.to}
                          to={c.to}
                          onClick={() => setOpenMenu(null)}
                          className="block px-5 py-3 hover:bg-[var(--gold)]/10 group/item border-l-2 border-transparent hover:border-[var(--gold)] transition-colors"
                        >
                          <p className="text-xs uppercase tracking-[0.2em] text-white group-hover/item:text-[var(--gold)]">
                            {c.label}
                          </p>
                          {c.desc && (
                            <p className="text-[11px] text-white/55 font-light mt-1">
                              {c.desc}
                            </p>
                          )}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={n.label}
                to={n.to!}
                className={`text-[11px] uppercase tracking-[0.25em] hover:text-[var(--gold)] transition-colors ${subCls}`}
                activeProps={{ className: "text-[var(--gold)]" }}
              >
                {n.label}
              </Link>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <LanguageSwitcher variant="light" />
          <a
            href="tel:0767908667"
            onClick={() => trackEvent("contact_click", { contact_method: "tel", cta_location: "header" })}
            className={`text-[11px] uppercase tracking-[0.25em] hover:text-[var(--gold)] transition-colors ${subCls} font-[Arial]`}
          >
            07 67 90 86 67
          </a>
          <Link
            to="/contactez-nous"
            onClick={() => trackEvent("cta_click", { cta_label: "Devis", cta_location: "header" })}
            className="bg-[var(--gold)] text-[var(--ink)] px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all"
          >
            Devis
          </Link>

        </div>

        <div className="lg:hidden flex items-center gap-3">
          <LanguageSwitcher variant="light" />
          <button aria-label="Menu" onClick={() => setOpen((v) => !v)} className={`hover:text-[var(--gold)] text-2xl ${textCls}`}>
            {open ? "✕" : "☰"}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#0a0a0a] backdrop-blur-md border-t border-[var(--gold-line)] max-h-[85vh] overflow-y-auto">
          {/* CTA prioritaires en haut */}
          <div className="px-6 pt-5 pb-4 flex gap-2 border-b border-[var(--gold-line)]">
            <Link
              to="/contactez-nous"
              onClick={() => setOpen(false)}
              className="flex-1 bg-[var(--gold)] text-[var(--ink)] px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-center"
            >
              Devis gratuit
            </Link>
            <a
              href="tel:0767908667"
              className="px-4 py-3 border border-[var(--gold)] text-[var(--gold)] text-[11px] font-semibold uppercase tracking-[0.2em]"
            >
              Appeler
            </a>
          </div>
          <nav className="flex flex-col px-2 py-3">
            {MOBILE_GROUPS.map((g) =>
              g.children ? (
                <details key={g.label} className="group border-b border-white/5 last:border-0">
                  <summary className="flex items-center justify-between px-4 py-3.5 text-xs uppercase tracking-[0.25em] text-white/85 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                    <span>{g.label}</span>
                    <span className="text-[10px] text-[var(--gold)] transition-transform group-open:rotate-180">▼</span>
                  </summary>
                  <div className="pb-2">
                    {g.children.map((c) => (
                      <Link
                        key={c.to}
                        to={c.to}
                        onClick={() => setOpen(false)}
                        className="block pl-8 pr-4 py-2.5 text-[12px] text-white/65 hover:text-[var(--gold)] hover:bg-[var(--gold)]/5"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </details>
              ) : (
                <Link
                  key={g.label}
                  to={g.to!}
                  onClick={() => setOpen(false)}
                  className="block px-4 py-3.5 text-xs uppercase tracking-[0.25em] text-white/85 hover:text-[var(--gold)] border-b border-white/5"
                  activeProps={{ className: "text-[var(--gold)]" }}
                  activeOptions={{ exact: g.to === "/" }}
                >
                  {g.label}
                </Link>
              )
            )}
          </nav>
        </div>
      )}
    </header>
  );
}


export function SiteFooter() {
  const year = new Date().getFullYear();
  return (
    <footer className="bg-card border-t border-[var(--gold-line)] text-muted-foreground">
      {/* Maillage interne — plan de site condensé */}
      <nav aria-label="Plan du site" className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 pb-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-8 gap-y-8 border-b border-[var(--gold-line)]">
        {FOOTER_LINK_GROUPS.map((g) => (
          <div key={g.title}>
            <h2 className="text-[10px] uppercase tracking-[0.3em] text-[var(--gold)] mb-4">{g.title}</h2>
            <ul className="space-y-2">
              {g.links.map((l) => (
                <li key={`${l.to}${l.params ? Object.values(l.params).join("") : ""}`}>
                  <Link
                    to={l.to}
                    params={l.params as never}
                    className="text-[13px] leading-snug hover:text-[var(--gold)] transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        {/* Brand + contact */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-x-8 gap-y-3">
          <Link to="/" className="font-serif-display text-2xl text-foreground italic leading-none">
            DJ <span className="text-[var(--gold)]">Val</span>
          </Link>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">
            <a href="tel:0767908667" onClick={() => trackEvent("contact_click", { contact_method: "tel", cta_location: "footer" })} className="hover:text-[var(--gold)] transition-colors font-[Arial] tabular-nums">07 67 90 86 67</a>
            <a href="mailto:info.djval@gmail.com" onClick={() => trackEvent("contact_click", { contact_method: "email", cta_location: "footer" })} className="hover:text-[var(--gold)] transition-colors">info.djval@gmail.com</a>
            <span className="text-muted-foreground text-xs">Cavaillon · Vaucluse</span>
          </div>

        </div>

        {/* CTA + réseaux */}
        <div className="flex items-center gap-4">
          <Link
            to="/contactez-nous"
            onClick={() => trackEvent("cta_click", { cta_label: "Devis gratuit", cta_location: "footer" })}
            className="inline-flex items-center px-5 py-2.5 bg-[var(--gold)] text-[var(--ink)] text-[11px] uppercase tracking-[0.3em] font-medium hover:brightness-110 transition-all"
          >
            Devis gratuit
          </Link>
          <div className="flex items-center gap-1 text-[var(--gold)] tracking-[0.3em] text-xs" aria-label="5 sur 5 étoiles, 41 avis Google">
            ★★★★★
          </div>
        </div>
      </div>

      <div className="border-t border-[var(--gold-line)] py-4 px-6">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-center items-center gap-x-5 gap-y-2 text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
          <span>© {year} DJ Val</span>
          <Link to="/plan-du-site" className="hover:text-[var(--gold)] transition-colors">Plan du site</Link>
          <Link to="/mentions-legales" className="hover:text-[var(--gold)] transition-colors">Mentions légales</Link>
          <Link to="/politique-confidentialite" className="hover:text-[var(--gold)] transition-colors">Confidentialité</Link>
          <Link to="/conditions-de-location" className="hover:text-[var(--gold)] transition-colors">CGV location</Link>
          <Link to="/en/wedding-dj-provence" hrefLang="en" className="hover:text-[var(--gold)] transition-colors">English</Link>

        </div>
      </div>
    </footer>
  );
}
